import { MaxBidModal } from '@components/BidModal/MaxBidModal'
import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../contexts/AuthContext'
import { delay } from '@components/BiddingButtons/functions'
import TagManager from 'react-gtm-module'
import TiktokPixel from 'tiktok-pixel'
import convertDollarStringToFloat from '@utilities/functions/convertDollarStringToFloat'
import pushKlaviyoBidEvent from '@utilities/functions/pushKlaviyoBidEvent'

const MaxBid = ({ item, updateItem, expired, handleBan }: any) => {
  const { id, maxBid, auctionId } = item
  const {
    instance,
    userData: { profile },
  } = useContext<any>(AuthContext)
  const [loadingMaxBid, setLoadingMaxBid] = useState<boolean>(false)
  const [showMaxModal, setShowMaxModal] = useState<boolean>(false)
  const [maxBidError, setMaxBidError] = useState<any>('')
  const [location, setLocation] = useState<any>()
  const [maxBidInput, setMaxBidInput] = useState<string>(
    maxBid ? `$${maxBid}` : ''
  )

  const getMaxBidButtonLabel = () => {
    if (loadingMaxBid) {
      return 'Bidding...'
    }
    return 'Set Max Bid'
  }

  const handleInput = (value: any) => {
    let val = value.replace(/[^0-9.]|\.(?=.*\.)/g, '')
    if (val[0] === '.') {
      val = val.replace('.', '$0.')
    }
    if (!val.includes('$')) {
      setMaxBidInput(`$${val}`)
    } else {
      setMaxBidInput(val)
    }
  }

  useEffect(() => {
    if (maxBid && maxBidInput !== `$${maxBid}`) {
      handleInput(String(maxBid))
    }
  }, [maxBid])

  const handleMaxBid = async (e: any) => {
    try {
      import('react-facebook-pixel')
        .then((x) => x.default)
        .then((ReactPixel) => {
          ReactPixel.init('340924541426797') // facebookPixelId
          ReactPixel.pageView()
          ReactPixel.trackCustom('maxbid', maxBidInput)
        })

      TiktokPixel.init('CO5BJN3C77U6IP9P2760', { email: profile.email }).then(
        () =>
          TiktokPixel.track('Max Bid', {
            content_id: id,
            content_type: 'product',
            value: convertDollarStringToFloat(maxBidInput),
            currency: 'USD',
            email: profile.email,
          })
      )

      e.stopPropagation()

      const { data } = await instance.get(
        `/auction/auctionDetails/${auctionId}`
      )
      setLocation(data.location)

      const currencyMatch = new RegExp('^[0-9]+(.[0-9]{1,2})?$')
      const isCurrency = currencyMatch.test(maxBidInput.slice(1))

      if (!isCurrency) {
        setMaxBidError('Currency must follow format: $x.xx')
        return
      }
      const attemptedBidAmount = Number(maxBidInput.slice(1))

      if (attemptedBidAmount <= item.nextBid) {
        setMaxBidError('Max Bid should be greater than next bid increment.')
        return
      }
      setMaxBidError('')
      setShowMaxModal(true)
    } catch (e: any) {
      console.error('Max Bid Dialog Error: ', JSON.stringify(e))
    }
  }

  const placeMaxBid = async () => {
    try {
      setLoadingMaxBid(true)
      const { data } = await instance.post(`/bid/placeMaxBidAuctionItems`, {
        auctionId,
        itemId: id,
        maxBid: maxBidInput === '' ? 0 : maxBidInput.slice(1),
      })
      if (data?.msg?.includes('Your account is currently suspended')) {
        handleBan('MaxBid')
      }
      if (data?.success !== false) {
        pushKlaviyoBidEvent({
          profile,
          item,
          location,
          maxBid: convertDollarStringToFloat(maxBidInput),
        })
        updateItem(data.item)
        TagManager.dataLayer({
          dataLayer: {
            event: 'handle-bidding',
            'data-bidding-next-bid': item.nextBid,
            'data-bidding-current-bid': item.currentBid,
          },
        })
        await delay(3000)
      }
      setLoadingMaxBid(false)
    } catch (e: any) {
      setLoadingMaxBid(false)
      console.error('Max Bid Error: ', JSON.stringify(e))
    }
  }

  return (
    <>
      <div
        className={`flex w-full transition duration-300 ease-in-out gap-4 justify-evenly ${
          loadingMaxBid ? ' opacity-25' : 'opacity-100'
        }`}
      >
        <MaxBidInput
          type="text"
          value={maxBidInput}
          placeholder="$0.00"
          onChange={(e: any) => handleInput(e.target.value)}
          onClick={(e: any) => e.stopPropagation()}
          onKeyDown={(e: any) => e.key === 'Enter' && placeMaxBid()}
          disabled={expired}
        />
        <button
          className={`text-15 w-full ${
            expired ? 'bg-gray-500' : 'bg-bidfta-yellow-light'
          } text-bidfta-gray-dark py-3 md:py-3 ${
            expired ? 'cursor-not-allowed' : ''
          } xl:py-3 font-bold border border-bidfta-yellow-border rounded-md flex justify-center items-center hover:bg-bidfta-yellow transition duration-300 ease-in-out`}
          onClick={handleMaxBid}
          disabled={expired}
        >
          {getMaxBidButtonLabel()}
        </button>
      </div>
      {maxBidError ? (
        <div className="mt-1 text-xs text-bidfta-red">{maxBidError}</div>
      ) : (
        ''
      )}
      <MaxBidModal
        visible={showMaxModal}
        item={{
          qty: item.quantity,
          bid: Number(maxBidInput === '' ? 0 : maxBidInput.slice(1)),
          location: location?.nickName,
        }}
        onYes={() => {
          placeMaxBid()
          setShowMaxModal(false)
        }}
        onNo={() => setShowMaxModal(false)}
      />
    </>
  )
}

const MaxBidInput = (props: any) => {
  const { className } = props

  return (
    <input
      {...props}
      className={`${className} w-full text-center text-15 text-bidfta-blue px-2.5 py-1.5 md:py-2 xl:py-2.5 block border border-bidfta-yellow-border rounded-md rounded-bl-md bg-white transition duration-300 ease-in-out`}
      style={{ outlineColor: '#D79A47' }}
    />
  )
}

export default MaxBid
